import { useEffect } from "react";
import Head from "next/head";
import Image from "next/image";

import Typography from "@mui/material/Typography";
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";

import MainHeader from "../components/MainHeader";
import MainFooter from "../components/MainFooter";
import DemoForm from "../components/forms/Form";
import YoutubeEmbed from "../components/YoutubeEmbed";

import { getCookie } from "cookies-next";

import fonts from "../styles/FontInter.module.css";
import mainTheme from "../components/mainTheme";
import styles from "../styles/Home.module.css";
import ExitModal from "../components/ExitModal";
import FormMini from "../components/forms/FormMini";

export default function TemplatePage({ utm }) {
  useEffect(() => {
    dataLayer.push({ event: "optimize.activate" });
  }, []);

  return (
    <ThemeProvider theme={mainTheme}>
      <Head>
        <title>VoicePad | Solutions - Elm Street Technology</title>
        <meta
          name="description"
          content="VoicePad - Revolutionize your business with cutting-edge telecom solutions and smarter lead generation tools."
        />
        <link rel="icon" href="/favicon.ico" />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:site" content="@weareelmstreet" />
        <meta name="twitter:creator" content="@weareelmstreet" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://elmstreet.com/morris-marketing-group" />
        <meta property="og:title" content="VoicePad | Solutions - Elm Street Technology" />
        <meta
          property="og:description"
          content="VoicePad - Revolutionize your business with cutting-edge telecom solutions and smarter lead generation tools."
        />
        <meta property="og:image" content="https://elmstreet.com/og_images/elmstreetog.jpg" />
      </Head>
      <CssBaseline className={fonts} />
      <MainHeader />

      <div
        style={{
          backgroundImage: "url('voicepad-header.png')",
          backgroundPosition: "center",
          backgroundSize: "cover",
          display: "block",
        }}
      >
        <Container maxWidth="lg">
          <Grid container>
            <Grid item md={5} sm={12}>
              <div
                style={{
                  color: "#FFFFFF",
                  alignItems: "center",
                  display: "flex",
                  flexDirection: "row",
                  height: "650px",
                }}
              >
                <div>
                  <Image
                    style={{ paddingTop: "50px", paddingLeft: "20px" }}
                    src="/voicepad-logo-light.svg"
                    alt={`VoicePad`}
                    height="100px"
                    width="300px"
                    unoptimized={true}
                  />
                  <Typography variant="h2" component="h1">
                    <span style={{ color: mainTheme.palette.secondary.main }}>
                      Telecom Solutions That Keep Real Estate Agents Connected and Competitive
                    </span>{" "}
                    <br />
                  </Typography>
                  <p>
                    Revolutionize your business with cutting-edge telecom solutions and smarter lead generation tools.
                  </p>
                  <Button variant="contained" color="secondary" href="#form">
                    schedule a demo
                  </Button>
                </div>
              </div>
            </Grid>
          </Grid>
        </Container>
      </div>

      <div>
        <Container
          maxWidth="lg"
          style={{
            paddingLeft: "20px",
            paddingRight: "20px",
            paddingTop: "45px",
            paddingBottom: "35px",
          }}
        >
          <div style={{ marginTop: "55px" }}>
            <Grid container spacing={8}>
              <Grid item md={6} sm={12}>
                <div>
                  <Image
                    src="/advanced-lead-generation.png"
                    width="1080px"
                    height="1066px"
                    alt="VoicePad - Advanced Lead Generation Solutions"
                  />
                </div>
              </Grid>
              <Grid item md={6} sm={12} style={{ paddingBottom: "20px" }}>
                <Typography variant="h4" component="h4">
                  <span style={{ color: "#f69c26" }}>Advanced Lead Generation Solutions</span>
                </Typography>
                <br />
                <span>
                  VoicePad combines innovative technology with real estate expertise to deliver powerful lead generation
                  tools. Our solutions connect you with motivated buyers and sellers through voice and text platforms,
                  making it easy to capture and qualify leads anytime, anywhere.
                  <br />
                  <br />
                  <Button variant="contained" color="secondary" href="#form">
                    Learn More
                  </Button>
                </span>
              </Grid>
            </Grid>
          </div>
        </Container>
      </div>

      <div>
        <Container
          maxWidth="lg"
          style={{
            paddingLeft: "20px",
            paddingRight: "20px",
            paddingBottom: "75px",
          }}
        >
          <div style={{ marginTop: "25px" }}>
            <Grid container spacing={8}>
              <Grid item md={6} sm={12}>
                <Typography variant="h4" component="h4">
                  <span style={{ color: "#f69c26" }}>Seamless Communication Made Simple</span>
                  <br />
                </Typography>

                <p>
                  In real estate, every conversation counts. VoicePad streamlines your communication with integrated
                  voice and SMS tools, ensuring you stay accessible to clients and leads. Whether answering inquiries or
                  providing property details, VoicePad keeps you connected with minimal effort.
                </p>
              </Grid>
              <Grid item md={6} sm={12}>
                <div>
                  <Image
                    src="/seamless-communication.png"
                    width="1080px"
                    height="1066px"
                    alt="VoicePad - Seamless Communication Made Simple"
                  />
                </div>
              </Grid>
            </Grid>
          </div>
        </Container>
      </div>

      <div>
        <Container
          maxWidth="lg"
          style={{
            paddingLeft: "20px",
            paddingRight: "20px",
            paddingBottom: "75px",
          }}
        >
          <div style={{ marginTop: "55px" }}>
            <Grid container spacing={8}>
              <Grid item md={6} sm={12}>
                <div>
                  <Image
                    src="/boost-productivity.png"
                    width="1080px"
                    height="1066px"
                    alt="VoicePad - Boost Productivity with Automation"
                  />
                </div>
              </Grid>
              <Grid item md={6} sm={12} style={{ paddingBottom: "20px" }}>
                <Typography variant="h4" component="h4">
                  <span style={{ color: "#f69c26" }}>Boost Productivity with Automation</span>
                  <br />
                </Typography>

                <p>
                  Time is money, and VoicePad helps you make the most of both. From automated lead follow-ups to
                  property detail recordings, our platform handles repetitive tasks, freeing you to focus on closing
                  deals. It’s like having a personal assistant working for you 24/7.
                </p>
              </Grid>
            </Grid>
          </div>
        </Container>
      </div>

      <div>
        <Container
          maxWidth="lg"
          style={{
            paddingLeft: "20px",
            paddingRight: "20px",
            paddingBottom: "75px",
          }}
        >
          <div style={{ marginTop: "25px" }}>
            <Grid container spacing={8}>
              <Grid item md={6} sm={12}>
                <Typography variant="h4" component="h4">
                  <span style={{ color: "#f69c26" }}>Built for Real Estate Professionals</span>
                  <br />
                </Typography>

                <p>
                  VoicePad isn’t just a telecom solution; it’s designed specifically for the unique challenges of real
                  estate. With features like instant call routing, and detailed lead tracking, our platform equips you
                  with everything you need to grow your business and stay ahead of the competition.
                </p>
              </Grid>
              <Grid item md={6} sm={12}>
                <div>
                  <Image
                    src="/built-for-real-estate.png"
                    width="1080px"
                    height="1066px"
                    alt="VoicePad - Built for Real Estate Professionals"
                  />
                </div>
              </Grid>
            </Grid>
          </div>
        </Container>
      </div>

      <div
        style={{
          backgroundImage: "url('orangebg.webp')",
          backgroundPosition: "center",
          backgroundSize: "cover",
          display: "block",
        }}
      >
        <Container maxWidth="lg" style={{ paddingTop: "80px", paddingBottom: "85px" }}>
          <div style={{}}>
            <a id="form" />
            <Typography variant="h4" component="h4" style={{ color: "#1d1746", textAlign: "center" }}>
              schedule a demo today
            </Typography>
            <div style={{ paddingBottom: "40px", textAlign: "center" }}>
              <strong style={{ color: "#1d1746" }}>
                Connect with Elm Street's Brokerage Success Team today to explore your path to success.
              </strong>
            </div>
            <div style={{ padding: "15px", backgroundColor: "#ffffff" }}>
              <Container
                maxWidth="lg"
                style={{
                  paddingLeft: "20px",
                  paddingRight: "20px",
                  marginTop: "65px",
                  marginBottom: "65px",
                }}
              >
                <Grid container>
                  <Grid item md={2} sm={12} xs={12} />
                  <Grid item md={8} sm={12} xs={12}>
                    <DemoForm
                      buttonText="Schedule Demo"
                      successMsg={
                        <div style={{ color: "#d37800" }}>
                          <strong>
                            We're Excited to Connect with You! A member of the Elm Street Technology team will be in
                            touch with you shortly.
                          </strong>
                          <br />
                          <br />
                        </div>
                      }
                      listId="147986"
                      formName="es2_brokersTeams"
                      fieldList={["first_name", "last_name", "email", "phone", "roll", "solutions", "currentClient"]}
                    />
                  </Grid>
                </Grid>
              </Container>
            </div>
          </div>
        </Container>
      </div>

      <MainFooter />
    </ThemeProvider>
  );
}

export const getServerSideProps = ({ req, res }) => {
  let cookies = getCookie("elmlead", { req, res });
  if (cookies) {
    return { props: { utm: cookies } };
  }
  return { props: {} };
};
